/**
 * Shared pause gate for the landing page's continuous animation drivers
 * (slow-mobile hardening, 2026-07-05 investigation).
 *
 * Every landing driver — the hero scene player, the command console, the
 * control/discovery streams, the platform interval tours — is an async loop
 * or interval that previously ran from mount FOREVER: scrolled past,
 * backgrounded tab, regardless (the hero scene player alone re-rendered its
 * whole subtree ~35×/sec). The gate closes while the driver's section is
 * outside the viewport OR the document is hidden:
 *
 *   - async loops thread their sleeps through {@link VisibilityGate.gatedWait}
 *     — the loop stalls at its next beat and resumes exactly where it left
 *     off (no state reset, no skipped beats);
 *   - interval drivers guard each tick on {@link VisibilityGate.active}.
 *
 * Environments without `IntersectionObserver` (jsdom, ancient engines) get a
 * permanently-open gate — the drivers behave exactly as before this gate
 * existed. `prefers-reduced-motion` handling stays in each driver (they
 * render a static frame and never start their loops).
 */

export interface VisibilityGate {
  /**
   * Section in view AND document visible.
   *
   * This is a POINT-IN-TIME read, and `IntersectionObserver` updates it
   * asynchronously — so it is correct to await or to read inside a loop beat,
   * and wrong to poll on scroll. A driver that needs to react to changes takes
   * the `onChange` callback instead.
   */
  readonly active: boolean;
  /** Resolves immediately when active; else on the next activation. */
  whenActive(): Promise<void>;
  /** `wait(ms)` that additionally stalls while the gate is closed. */
  gatedWait(ms: number): Promise<void>;
  /** Disconnect observers and release any pending waiters. */
  dispose(): void;
}

/** How far outside the viewport a section still counts as "in view" — avoids
 *  stutter when a driver's section sits right at the fold while scrolling. */
const IN_VIEW_MARGIN = '160px';

/**
 * Optional change notification.
 *
 * An async loop does not need it — it holds the gate in a closure and awaits
 * `gatedWait`. A React driver does: it re-evaluates from state, so it has to be
 * TOLD when the gate flips. Polling the `active` getter is not a substitute and
 * was tried first: `IntersectionObserver` delivers asynchronously, so a poll
 * driven by scroll events reads the state from BEFORE the scroll — which
 * produced a gate that was closed while the section sat in view and open once
 * the reader had scrolled away, i.e. exactly backwards.
 */
export type VisibilityGateListener = (active: boolean) => void;

export function createVisibilityGate(
  target: Element | null,
  onChange?: VisibilityGateListener,
): VisibilityGate {
  const ioSupported = typeof IntersectionObserver !== 'undefined';
  // No observer support or no target → never gate on section visibility.
  let sectionVisible = !ioSupported || target === null;
  let documentVisible = typeof document === 'undefined' ? true : !document.hidden;
  let disposed = false;
  let waiters: (() => void)[] = [];

  const isActive = (): boolean => disposed || (sectionVisible && documentVisible);

  const release = (): void => {
    const pending = waiters;
    waiters = [];
    for (const resolve of pending) resolve();
  };

  /**
   * Emit on a real transition — and ALWAYS on the first observation.
   *
   * The two halves of this gate start from opposite assumptions, and that is
   * deliberate on both sides. A listener assumes OPEN, so a driver is never
   * stalled by a gate that has not attached yet (see `useVisibilityGate`: a
   * beat started late is invisible, a beat never started is a dead demo). The
   * gate assumes CLOSED, because an observed target is not visible until the
   * observer says so.
   *
   * De-duplicating the first emit against the gate's own initial value left
   * those two silently disagreeing, in the one direction nobody looks at. A
   * section that begins below the fold gets `isIntersecting: false` as its
   * first report; that equalled `lastEmitted`, so nothing was emitted, so the
   * listener kept the optimistic `true` it was born with — for the session.
   * Measured on the landing page at 1440x900 (2026-08-18): the how-it-works
   * carousel advanced 1 -> 2 -> 0 -> 1 on a 6.5s interval, and its chat
   * simulator typed a full conversation, with the band 900px below the fold
   * and never once seen. The gate looked wired and was inert.
   *
   * So the first settle always tells the listener where it stands; dedupe
   * applies from the second onward, which is what keeps a scroll across the
   * rootMargin from re-rendering for nothing.
   */
  let lastEmitted = isActive();
  let hasEmitted = false;
  const settle = (): void => {
    if (isActive()) release();
    if (disposed) return;
    const now = isActive();
    if (hasEmitted && now === lastEmitted) return;
    hasEmitted = true;
    lastEmitted = now;
    onChange?.(now);
  };

  const onVisibilityChange = (): void => {
    documentVisible = !document.hidden;
    settle();
  };

  let observer: IntersectionObserver | null = null;
  if (ioSupported && target !== null) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          sectionVisible = entry.isIntersecting;
        }
        settle();
      },
      { rootMargin: IN_VIEW_MARGIN },
    );
    observer.observe(target);
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  const whenActive = (): Promise<void> => {
    if (isActive()) return Promise.resolve();
    return new Promise((resolve) => {
      waiters.push(resolve);
    });
  };

  return {
    get active(): boolean {
      return isActive();
    },
    whenActive,
    gatedWait: (ms) =>
      new Promise<void>((resolve) => {
        setTimeout(resolve, ms);
      }).then(whenActive),
    dispose: () => {
      disposed = true;
      observer?.disconnect();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      // A disposed gate is "open": cancelled loops must be able to run into
      // their own isCancelled() checks instead of hanging on a dead gate.
      release();
    },
  };
}
